import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Text,
  Flex,
  Input,
  Switch,
  Checkbox,
} from '@chakra-ui/react';
import { SelectPicker, Button as RsButton } from 'rsuite';
import { useFeatureFlagFromContext } from '../../../launchdarkly';
import ReskinnedAddIntegrationModal from './ReskinnedAddIntegrationModal';
import ReskinnedAddIntegrationFlowModal from './ReskinnedAddIntegrationFlowModal';
import '../../../styles/reskinned-integration.css';

/**
 * Add Integration Modal Component
 */
const AddIntegrationModal = ({
  isOpen,
  onClose,
  onSave,
  saveBtnLoading,
  formState,
  setFormState,
  country,
  integrationTypes,
  periodTypes,
  defaultPeriod,
  cashAccData,
  allIntegrationTypes,
  companySwitcherActive,
  apps,
  isFirstIntegration,
  companyName,
}) => {
  // Check if the showReskin feature flag is enabled
  const showReskin = useFeatureFlagFromContext('showReskin', false);
  // Check if the simplified integration flow feature flag is enabled
  const simplifiedIntegrationFlow = useFeatureFlagFromContext(
    'simplifiedIntegrationFlow',
    false
  );

  // If showReskin is enabled and simplifiedIntegrationFlow is enabled, render the new flow modal
  if (showReskin && simplifiedIntegrationFlow) {
    return (
      <ReskinnedAddIntegrationFlowModal
        isOpen={isOpen}
        onClose={onClose}
        onSave={onSave}
        defaultPeriod={defaultPeriod}
        allIntegrationTypes={allIntegrationTypes}
        isFirstIntegration={isFirstIntegration}
        apps={apps}
        companyName={companyName}
      />
    );
  }

  // If showReskin is enabled, render the reskinned version
  if (showReskin) {
    return (
      <ReskinnedAddIntegrationModal
        isOpen={isOpen}
        onClose={onClose}
        onSave={onSave}
        saveBtnLoading={saveBtnLoading}
        formState={formState}
        setFormState={setFormState}
        country={country}
        integrationTypes={integrationTypes}
        periodTypes={periodTypes}
        defaultPeriod={defaultPeriod}
        cashAccData={cashAccData}
        allIntegrationTypes={allIntegrationTypes}
        companySwitcherActive={companySwitcherActive}
        apps={apps}
        isFirstIntegration={isFirstIntegration}
      />
    );
  }

  // Otherwise, render the original version
  const {
    appName = '',
    captureLocation,
    locationAttr,
    periodCalTypeFlag,
    attributeType,
    captureLocationDisplay,
    locationAttrDisplay,
  } = formState;

  const handleIntegrationTypeChange = value => {
    // Find the selected integration type
    const selectedIntegrationType = allIntegrationTypes.find(
      item => item.integration_type === value
    );

    // Update individual state properties
    if (selectedIntegrationType) {
      // First update the integration_type
      setFormState({
        ...formState,
        integration_type: value,
        tpc: selectedIntegrationType.tpc,
        captureLocationDisplay: selectedIntegrationType.capture_location,
        locationAttrDisplay: selectedIntegrationType.location_attr,
        hotglueFlowId: selectedIntegrationType.flow_id,
        sourceId: selectedIntegrationType.source_id,
      });
    } else {
      // If no matching integration type is found, just update the integration_type
      setFormState({
        ...formState,
        integration_type: value,
      });
    }
  };

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'sm', md: 'lg', lg: '3xl' }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader textAlign="center" fontSize="lg" fontWeight="bold">
          Add Integrations
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex direction="column" gap={4}>
            {/* App Name Input */}
            <FormControl>
              <FormLabel>
                <Text fontSize="sm">App Name</Text>
              </FormLabel>
              <Input
                type="text"
                id="appName"
                placeholder="Enter App Name"
                size="sm"
                value={appName}
                onChange={e =>
                  setFormState({ ...formState, appName: e.target.value })
                }
              />
            </FormControl>

            {/* Country Selector */}
            <FormControl>
              <FormLabel>
                <Text fontSize="sm">Country</Text>
              </FormLabel>
              <SelectPicker
                className={'reskin-country-selector'}
                data={country}
                menuStyle={{
                  zIndex: 9999,
                  maxHeight: '100%',
                  overflowX: 'auto',
                  maxWidth: '200px',
                }}
                style={{ width: '100%', height: '46px' }}
                placeholder="Select Country"
                onChange={value => {
                  setFormState({ ...formState, countrySelected: value });
                }}
              />
            </FormControl>

            {/* Integration Type Selector */}
            <FormControl>
              <FormLabel>
                <Text fontSize="sm">Integration Type</Text>
              </FormLabel>
              <SelectPicker
                className={'reskin-select-picker'}
                data={integrationTypes}
                menuStyle={{
                  zIndex: 9999,
                  maxHeight: '100%',
                  overflowX: 'auto',
                  maxWidth: '200px',
                }}
                style={{ width: '100%', height: '46px' }}
                placeholder="Select Integration Type"
                onChange={handleIntegrationTypeChange}
              />
            </FormControl>

            {/* Period Calendar Options */}
            {companySwitcherActive && (
              <Flex direction="column" gap={2}>
                <FormControl>
                  <Checkbox
                    id="periodCal"
                    onChange={() => {
                      setFormState({
                        ...formState,
                        periodCalTypeFlag: !periodCalTypeFlag,
                        periodCalType: defaultPeriod,
                      });
                    }}
                  >
                    <Text fontSize="sm">Activate Periods</Text>
                  </Checkbox>
                </FormControl>
                <SelectPicker
                  className={'reskin-select-picker'}
                  data={periodTypes}
                  menuStyle={{
                    zIndex: 9999,
                    maxHeight: '100%',
                    overflowX: 'auto',
                    maxWidth: '200px',
                  }}
                  style={{ width: '100%', height: '46px' }}
                  placeholder={defaultPeriod}
                  disabled={!periodCalTypeFlag}
                  defaultValue={defaultPeriod}
                  onChange={value =>
                    setFormState({ ...formState, periodCalType: value })
                  }
                />
              </Flex>
            )}

            {/* Cash or Accrual Selector */}
            <FormControl>
              <FormLabel>
                <Text fontSize="sm">Accounting Type</Text>
              </FormLabel>
              <SelectPicker
                className={'reskin-select-picker'}
                data={cashAccData}
                menuStyle={{
                  zIndex: 9999,
                  maxHeight: '100%',
                  overflowX: 'auto',
                  maxWidth: '200px',
                }}
                style={{ width: '100%', height: '46px' }}
                placeholder="Select Cash or Accrual"
                onChange={value =>
                  setFormState({ ...formState, cashAc: value })
                }
              />
            </FormControl>

            {/* Additional Drilldown Options */}
            {captureLocationDisplay && (
              <Flex direction="row" gap={4}>
                <FormControl>
                  <FormLabel>
                    <Text fontSize="sm">Additional Drilldown</Text>
                  </FormLabel>
                  <Switch
                    onChange={() =>
                      setFormState({
                        ...formState,
                        captureLocation: !captureLocation,
                      })
                    }
                    isDisabled={!captureLocationDisplay}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>
                    <Text fontSize="sm">
                      {attributeType ? 'Location' : 'Class'} Attribute
                    </Text>
                  </FormLabel>
                  <Switch
                    onChange={() =>
                      setFormState({
                        ...formState,
                        locationAttr: !locationAttr,
                        attributeType: !attributeType,
                      })
                    }
                    isDisabled={!locationAttrDisplay}
                  />
                </FormControl>
              </Flex>
            )}
          </Flex>
        </ModalBody>

        <ModalFooter>
          <Flex w="100%" justify="space-between" gap={4}>
            <RsButton
              colorScheme="blue"
              onClick={onSave}
              isLoading={saveBtnLoading}
              flex={1}
              style={{ height: '40px', width: '50%' }}
              appearance="primary"
            >
              Save
            </RsButton>
            <RsButton
              onClick={onClose}
              flex={1}
              style={{ height: '40px', width: '50%' }}
              appearance="default"
            >
              Cancel
            </RsButton>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddIntegrationModal;
